import type { ResolvedCommand, WhenContext } from '@hibro/shared';

import {
  type CommandCandidate,
  isListedCommand,
  queryCommands,
  type TitleResolver,
} from '../../commands/command-query';
import { bestMatchScore } from '../relevance';
import {
  COMMAND_PREFIX,
  PROVIDER_RANK,
  type SearchProvider,
  type SearchResult,
  SECTION_COMMAND,
} from '../search-contract';

export interface CommandProviderDeps {
  /** Every known command, built-ins included. */
  commands: readonly ResolvedCommand[];
  /** Context the commands' `enablement` clauses are evaluated against. */
  context: WhenContext;
  resolveTitle: TitleResolver;
  /** Recently used command ids, most recent first. Feeds ranking. */
  mru: readonly string[];
  /**
   * Chord hint per command id, already spelled for this platform and keyboard
   * layout (`⌘⇧P`).
   *
   * Pre-formatted rather than the raw bindings: the hint has to show the chord
   * the user would actually press, which means it has already been through the
   * merge with their overrides and through `formatChord` — neither of which
   * belongs in a search provider.
   */
  chordHints: ReadonlyMap<string, string>;
}

/** Bonus for a recently used command; small enough not to jump a match tier. */
const MRU_BONUS = 30;
/** Score floor for the empty box, so recency drives the "常用命令" listing. */
const RECENT_BASE = 1000;
/** How much worse a hit on the secondary text is than one on the title. */
const LABEL_PENALTY = 50;
const DESCRIPTION_PENALTY = 120;
/**
 * A command that matched only through its `keywords` or id — `queryCommands`
 * folds those into matching without exposing them, so there is no text here to
 * score. It still beats nothing, and stays below any real title hit.
 */
const KEYWORD_ONLY_SCORE = 10;

/**
 * How a declared command is written out for the user: its owning module's prefix
 * dropped, the remaining segments joined with `-`, under the mode prefix — so
 * `im.aaa.bbb` reads as `/aaa-bbb`. The module is already stated by the row's
 * provenance chip, and the dotted id is an internal address, not something worth
 * showing verbatim.
 *
 * **A display convention, not an address.** `queryCommands` matches against the
 * raw id, so a multi-segment spelling is not necessarily typeable — this is what
 * the row *shows*, not what the box parses.
 *
 * The short form is not always unique: `wallet.open` and `browser.open` both
 * collapse to `/open`. {@link resolveCommandSpellings} puts the module segment
 * back on those rows only.
 */
export function commandSpelling(id: string, moduleId: string): string {
  const scoped = id.startsWith(`${moduleId}.`)
    ? id.slice(moduleId.length + 1)
    : id;
  const segments = scoped.split('.').filter((segment) => segment.length > 0);
  // An id that is nothing but its module prefix has no tail to show; fall back
  // to the whole thing rather than emitting a bare `/`.
  return `${COMMAND_PREFIX}${segments.length > 0 ? segments.join('-') : id}`;
}

/**
 * Spellings actually drawn on command rows.
 *
 * Unique short forms stay short. A short form shared by two listed commands
 * keeps each owner's module (`/wallet-open`, `/browser-open`) — otherwise the
 * trailing edge, which is the only place the instruction is written, shows the
 * same text on both rows. Hidden commands do not count: they never get a row,
 * so they must not force a neighbour to grow. Enablement is ignored on purpose;
 * a spelling that flips when context changes is a second kind of collision.
 */
export function resolveCommandSpellings(
  commands: readonly Pick<ResolvedCommand, 'id' | 'moduleId' | 'internal'>[],
): ReadonlyMap<string, string> {
  const counts = new Map<string, number>();
  const shorts = new Map<string, { short: string; moduleId: string }>();
  for (const command of commands) {
    if (!isListedCommand(command)) {
      continue;
    }
    const short = commandSpelling(command.id, command.moduleId);
    shorts.set(command.id, { short, moduleId: command.moduleId });
    counts.set(short, (counts.get(short) ?? 0) + 1);
  }
  const spellings = new Map<string, string>();
  for (const [id, { short, moduleId }] of shorts) {
    spellings.set(
      id,
      (counts.get(short) ?? 0) > 1
        ? qualifiedCommandSpelling(short, moduleId)
        : short,
    );
  }
  return spellings;
}

/** Short form plus its module, unless the short form already leads with it. */
function qualifiedCommandSpelling(short: string, moduleId: string): string {
  const tail = short.startsWith(COMMAND_PREFIX)
    ? short.slice(COMMAND_PREFIX.length)
    : short;
  if (tail === moduleId || tail.startsWith(`${moduleId}-`)) {
    return short;
  }
  return `${COMMAND_PREFIX}${moduleId}-${tail}`;
}

/**
 * Relevance for one command row.
 *
 * With no query there is no relevance to speak of, so recency drives the order —
 * that is what makes the empty box read as "常用命令" (需求 §4.2.1).
 */
function scoreCommand(
  candidate: CommandCandidate,
  terms: readonly string[],
  mruRank: ReadonlyMap<string, number>,
): number {
  if (terms.length === 0) {
    return RECENT_BASE - (mruRank.get(candidate.id) ?? RECENT_BASE);
  }
  const matched = bestMatchScore(
    [
      { text: candidate.title },
      { text: candidate.label, penalty: LABEL_PENALTY },
      { text: candidate.description, penalty: DESCRIPTION_PENALTY },
    ],
    terms,
  );
  const base = matched > 0 ? matched : KEYWORD_ONLY_SCORE;
  return base + (candidate.recent ? MRU_BONUS : 0);
}

/**
 * Commands as search rows (架构方案 §5.9).
 *
 * A thin adapter: {@link queryCommands} stays the single authority on *what
 * matches* — listed-command / `enablement` filtering, keyword aliases, label
 * collisions — exactly as it was for the palette. Only the shape and the
 * ordering among matches change here.
 *
 * On an empty box this yields the MRU-ordered list, which is what the "常用命令"
 * group in 需求 §4.2.1 asks for once the face applies its row cap; under the `/`
 * prefix with nothing after it, the full list — the old palette behaviour, now
 * reachable only in command mode.
 */
export function createCommandProvider(
  deps: () => CommandProviderDeps,
): SearchProvider {
  return commandProvider(deps);
}

/**
 * The same commands, narrowed to one app's tab (需求 §5.2).
 *
 * An app tab is a **scope filter, not a different feature set**: scoping to 聊天
 * and typing `/` has to list IM's commands, not nothing. Without this the tab
 * answered only with the sections that app declared through `contributes.search`,
 * so command mode came up empty there while the empty state was still advising
 * 「输入 / 查看可用命令」 — the box contradicting itself (实测报过).
 *
 * Only ownership differs from {@link createCommandProvider}; matching, ranking
 * and the listed-command / `enablement` filtering stay `queryCommands`' job, so the
 * two tabs can never disagree about what a command is or whether it is listed.
 */
export function createAppCommandProvider(
  moduleId: string,
  deps: () => CommandProviderDeps,
): SearchProvider {
  return commandProvider(deps, moduleId);
}

function commandProvider(
  deps: () => CommandProviderDeps,
  /** Owning app, for an app tab's provider; absent = the platform-wide one. */
  scope?: string,
): SearchProvider {
  const id = scope === undefined ? 'command' : `command:${scope}`;
  return {
    id,
    section: SECTION_COMMAND,
    ...(scope === undefined ? {} : { moduleId: scope }),
    prefixes: [COMMAND_PREFIX],
    rank: PROVIDER_RANK.command,
    search: (query) => {
      const { commands, context, resolveTitle, mru, chordHints } = deps();
      // The whole table, not the scoped slice: an app tab has to spell a
      // command the same way All does, or `wallet.open` reads `/open` on its
      // own tab and `/wallet-open` once the sibling shows up beside it.
      const spellings = resolveCommandSpellings(commands);
      const mruRank = new Map(mru.map((id, index) => [id, index]));
      const candidates = queryCommands({
        commands:
          scope === undefined
            ? commands
            : commands.filter((command) => command.moduleId === scope),
        query: query.terms.join(' '),
        context,
        resolveTitle,
        mru,
      });
      const limited =
        query.limit === undefined
          ? candidates
          : candidates.slice(0, query.limit);
      // Relevance, not the order `queryCommands` happened to return: that one is
      // MRU-then-alphabetical, which buries a good match for a short query under
      // unrelated commands that sort earlier (实测报过). `queryCommands` stays the
      // authority on *what matches*; ranking among the matches is scored here.
      return limited.map<SearchResult>((candidate) => {
        // Name (with its category tag beside it) on top, what the command
        // *does* underneath, how to reach it at the trailing edge. A command
        // whose title resolved to nothing leads with the spelling instead, and
        // then the trailing copy is dropped rather than printing it twice.
        const named = candidate.title.trim().length > 0;
        const spelling =
          spellings.get(candidate.id) ??
          commandSpelling(candidate.id, candidate.moduleId);
        return {
          key: candidate.id,
          providerId: id,
          section: SECTION_COMMAND,
          title: named ? candidate.title : spelling,
          ...(candidate.description === undefined
            ? {}
            : { subtitle: candidate.description }),
          ...(named ? { trailing: spelling } : {}),
          ...(candidate.icon === undefined ? {} : { icon: candidate.icon }),
          // The tag carries provenance, not identity: with the command spelled
          // out at the row's trailing edge, the raw id that used to
          // disambiguate colliding labels would only say the same thing twice.
          badge: candidate.category ?? candidate.moduleId,
          ...(chordHints.has(candidate.id)
            ? { keybinding: chordHints.get(candidate.id) }
            : {}),
          score: scoreCommand(candidate, query.terms, mruRank),
          action: { type: 'command', command: candidate.id },
        };
      });
    },
  };
}

/**
 * Command spellings the unknown-command state can suggest (需求 §4.2.3): the
 * translated title and the raw id, both of which a user may have been aiming
 * at. Declared aliases live in `keywords`, which `queryCommands` folds into
 * matching without exposing — so they help find a command that *does* exist,
 * but cannot be offered back as a suggested spelling.
 */
export function commandSuggestionNames(
  deps: Pick<CommandProviderDeps, 'commands' | 'context' | 'resolveTitle'>,
): { name: string; value: ResolvedCommand }[] {
  const names: { name: string; value: ResolvedCommand }[] = [];
  for (const candidate of queryCommands({
    commands: deps.commands,
    query: '',
    context: deps.context,
    resolveTitle: deps.resolveTitle,
  })) {
    const command = deps.commands.find((entry) => entry.id === candidate.id);
    if (!command) {
      continue;
    }
    names.push({ name: candidate.title, value: command });
    names.push({ name: candidate.id, value: command });
  }
  return names;
}
